<template>
  <article class="pie-card">
    <header class="card-header">
      <h3 class="card-title">{{ title || 'Data Overview' }}</h3>
    </header>

    <div class="card-body">
      <div v-show="hasData && !loading" ref="chart" class="chart-canvas" />
      <div v-if="loading" class="chart-loading">
        <span class="pie-sk-ring"></span>
      </div>
      <div v-else-if="!hasData" class="chart-empty">
        <v-icon size="32" color="#4a5a60">mdi-chart-pie</v-icon>
        <span>No data available</span>
      </div>
    </div>
  </article>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'PieChartComponent',
  props: {
    title: { type: String, default: "" },
    dataFetcher: { type: Function, default: null }
  },
  data() {
    return {
      chartInstance: null,
      chartData: [],
      loading: true,
      resizeObserver: null,
      resizeFrame: null,
    };
  },
  computed: {
    hasData() {
      return Array.isArray(this.chartData) && this.chartData.length > 0;
    }
  },
  async mounted() {
    try {
      if (this.dataFetcher != null) {
        this.chartData = await this.dataFetcher();
      }
    } catch (error) {
      console.error(`Error fetching data for ${this.title}`, error);
    } finally {
      this.loading = false;
    }

    // Only build the ECharts instance when there's data — otherwise it renders an
    // empty grey ring; we show the centered empty state instead.
    if (this.hasData) {
      await this.$nextTick();
      this.initChart();
      this.observeResize();
    }
  },
  beforeUnmount() {
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  watch: {
    chartData() {
      this.updateChart();
    }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart) return;
      this.chartInstance = echarts.init(this.$refs.chart);
      this.updateChart();
    },
    updateChart() {
      if (!this.chartInstance) return;
      this.chartInstance.setOption({
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [{
          name: this.title || 'Data',
          type: 'pie',
          radius: ['45%', '75%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          data: this.chartData,
          itemStyle: {
            borderColor: '#1a262d',
            borderWidth: 2,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 12,
              shadowOffsetX: 0,
              shadowColor: 'rgba(100, 255, 218, 0.35)',
            },
          },
          label: {
            color: '#cfd8dc',
            fontSize: 12,
          },
          labelLine: {
            lineStyle: { color: '#4a5a60' },
          },
        }],
      });
    },
    observeResize() {
      if (typeof ResizeObserver === 'undefined' || !this.$refs.chart) return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeFrame) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          if (this.chartInstance) {
            this.chartInstance.resize();
          }
        });
      });
      this.resizeObserver.observe(this.$refs.chart);
    },
  },
};
</script>

<style scoped>
.pie-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.card-header {
  padding: 16px 18px 8px;
  flex: 0 0 auto;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.card-body {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  padding: 4px 12px 14px;
}

.chart-canvas {
  width: 100%;
  height: 100%;
  min-height: 220px;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8aa0aa;
  font-size: 0.9rem;
  pointer-events: none;
}

/* Loading: a shimmering donut ring while data is fetched. */
.chart-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.pie-sk-ring {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.04));
  -webkit-mask: radial-gradient(circle, transparent 44px, #000 45px);
  mask: radial-gradient(circle, transparent 44px, #000 45px);
  animation: pie-spin 1.1s linear infinite;
}

@keyframes pie-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .pie-sk-ring { animation: none; }
}

@media (max-width: 600px) {
  .card-header {
    padding: 14px 16px 6px;
  }

  .chart-canvas {
    min-height: 260px;
  }
}
</style>
